import type { Dateable } from 'pkg/api/models/dateable';
import type {
	PaymentMethodData,
	PaymentMethodType,
} from 'pkg/api/models/payment_method';
import type { Record } from 'pkg/api/models/record';

export enum OrderChargeStatus {
	OrderChargeStatusUnknown = 'unknown',
	OrderChargeStatusCompleted = 'completed',
	OrderChargeStatusPending = 'pending',
	OrderChargeStatusFailed = 'failed',
	// Its refunds cover all of it. Undoing one of those refunds makes it Completed again.
	OrderChargeStatusRefunded = 'refunded',
}

export enum OrderChargeMethod {
	OrderChargeMethodACHCreditTransfer = 'ach_credit_transfer',
	OrderChargeMethodACHDebit = 'ach_debit',
	OrderChargeMethodACSSDebit = 'acss_debit',
	OrderChargeMethodAlipay = 'alipay',
	OrderChargeMethodAUBECSDebit = 'au_becs_debit',
	OrderChargeMethodBACSDebit = 'bacs_debit',
	OrderChargeMethodBancontact = 'bancontact',
	OrderChargeMethodCard = 'card',
	OrderChargeMethodCardPresent = 'card_present',
	OrderChargeMethodEPS = 'eps',
	OrderChargeMethodFPX = 'fpx',
	OrderChargeMethodGiropay = 'giropay',
	OrderChargeMethodGrabpay = 'grabpay',
	OrderChargeMethodIDEAL = 'ideal',
	OrderChargeMethodInteracPresent = 'interac_present',
	OrderChargeMethodKlarna = 'klarna',
	OrderChargeMethodMultibanco = 'multibanco',
	OrderChargeMethodOther = 'other',
	OrderChargeMethodP24 = 'p24',
	OrderChargeMethodSEPADebit = 'sepa_debit',
	OrderChargeMethodSofort = 'sofort',
	OrderChargeMethodStripeAccount = 'stripe_account',
	OrderChargeMethodUnknown = 'unknown',
	OrderChargeMethodWeChat = 'wechat',
}

export enum OrderChargeFailureReason {
	OrderChargeFailureReasonAuthenticationRequired = 'authentication_required',
	OrderChargeFailureReasonCardNotSupported = 'card_not_supported',
	OrderChargeFailureReasonIncorrectCardNumber = 'incorrect_card_number',
	OrderChargeFailureReasonIncorrectCvc = 'incorrect_cvc',
	OrderChargeFailureReasonExpiredCard = 'expired_card',
	OrderChargeFailureReasonInsufficientFunds = 'insufficient_funds',
	OrderChargeFailureReasonCardLimitExceeded = 'card_limit_exceeded',
	OrderChargeFailureReasonGenericDecline = 'generic_decline',
	OrderChargeFailureReasonFraudulent = 'fraudulent',
	OrderChargeFailureReasonUnknownError = 'unknown_error',
	OrderChargeFailureReasonNoPaymentMethod = 'no_payment_method',
	OrderChargeFailureReasonBankRejected = 'bank_rejected',
	OrderChargeFailureReasonBankInsufficientFunds = 'bank_insufficient_funds',
	OrderChargeFailureReasonBankAccountClosed = 'bank_account_closed',
	OrderChargeFailureReasonIncorrectBankDetails = 'incorrect_bank_details',
	OrderChargeFailureReasonBankUnauthorised = 'bank_unauthorised',

	// OrderChargeFailedReasonTestError is used when simulating payment error in dev
	OrderChargeFailedReasonTestError = 'test_error',
}

export enum ChargeSourceType {
	Provider = 'gpc',
	Wallet = 'wallet',
	OutOfBand = 'out_of_band',
}

export interface OrderChange extends Record, Dateable {
	groupId: number;
	orderId: number;
	paymentProviderId: string;
	amount: number;
	status: OrderChargeStatus;
	method: OrderChargeMethod;
	failureReason: OrderChargeFailureReason;
	failureMessage: string;
	initiatedAt: number;
	completedAt: number;
	sourceType: ChargeSourceType;
	sourceId: number | null;
	walletType?: string;
	walletDisplayName?: string;

	// Fritidskortet return references from EHM's /signal response: OCR
	// identifies the disbursement on the club's bank statement; Bankgiro is
	// E-hälsomyndigheten's payee account. Both are needed when an admin wires
	// a manual refund back to EHM. Absent on non-FK charges or on FK charges
	// captured before the signal-handler wiring landed.
	fritidskortetReturnOcr?: string;
	fritidskortetReturnBankgiro?: string;

	paymentMethodType?: PaymentMethodType;
	paymentMethodData?: PaymentMethodData;
	description?: string;

	// What can still be refunded from this charge. Set on the order's own page.
	refundableLeft?: number;
}
